// Site Context — manages the user's active site and provides site switching
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { useAuth } from '@/context/AuthContext';

const SiteContext = createContext(null);

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user's sites when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setSites([]);
      setActiveSite(null);
      setLoading(false);
      return;
    }

    const fetchSites = async () => {
      try {
        const res = await api.get('/api/v1/auth/my-sites');
        const siteList = res.data || [];
        setSites(siteList);

        // Determine active site:
        // 1. Check localStorage for a previously selected site
        // 2. Fall back to the user's primary site_id
        // 3. Fall back to the first site in the list
        const savedSiteId = localStorage.getItem('emr_active_site_id');
        const match = siteList.find(s => s.id === savedSiteId)
          || siteList.find(s => s.id === user?.site_id)
          || siteList[0]
          || null;

        setActiveSite(match);
        if (match) {
          localStorage.setItem('emr_active_site_id', match.id);
        }
      } catch {
        // API may not be available yet or user has no sites
        setSites([]);
        setActiveSite(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSites();
  }, [isAuthenticated, user?.site_id]);

  // Switch active site — persists to localStorage and triggers a re-render
  // across all consuming components so data reloads for the new site
  const switchSite = useCallback((siteOrId) => {
    const site = typeof siteOrId === 'string'
      ? sites.find(s => s.id === siteOrId)
      : siteOrId;

    if (!site) return;

    setActiveSite(site);
    localStorage.setItem('emr_active_site_id', site.id);
  }, [sites]);

  const value = {
    sites,
    activeSite,
    activeSiteId: activeSite?.id || null,
    activeSiteName: activeSite?.name || 'No Site',
    hasMutipleSites: sites.length > 1,
    loading,
    switchSite,
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export default SiteContext;
